import { forwardRef, type ReactNode } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";

type Variant = "primary" | "secondary" | "ghost" | "destructive";
type Size = "sm" | "md" | "lg";

export type ButtonProps = Omit<HTMLMotionProps<"button">, "ref"> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

const SIZE: Record<Size, { h: number; px: number; fs: number; radius: number }> = {
  sm: { h: 30, px: 12, fs: 14, radius: 8 },
  md: { h: 38, px: 16, fs: 15, radius: 10 },
  lg: { h: 50, px: 20, fs: 17, radius: 12 },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "lg",
    loading,
    leadingIcon,
    trailingIcon,
    children,
    className = "",
    disabled,
    style,
    ...rest
  },
  ref,
) {
  const s = SIZE[size];

  const palette: { bg: string; color: string; border?: string } = (() => {
    switch (variant) {
      case "primary":
        return { bg: "var(--system-blue)", color: "#FFFFFF" };
      case "secondary":
        return {
          bg: "var(--fill-tertiary)",
          color: "var(--system-blue)",
        };
      case "ghost":
        return { bg: "transparent", color: "var(--system-blue)" };
      case "destructive":
        return { bg: "var(--system-red)", color: "#FFFFFF" };
    }
  })();

  return (
    <motion.button
      ref={ref}
      whileTap={disabled || loading ? undefined : { scale: 0.97 }}
      transition={{ type: "spring", stiffness: 420, damping: 28 }}
      className={[
        "inline-flex items-center justify-center gap-2 select-none",
        "transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--system-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-system)]",
        className,
      ].join(" ")}
      style={{
        height: s.h,
        paddingInline: s.px,
        fontSize: s.fs,
        borderRadius: s.radius,
        background: palette.bg,
        color: palette.color,
        ...style,
      }}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {leadingIcon && <span className="shrink-0">{leadingIcon}</span>}
          {children}
          {trailingIcon && <span className="shrink-0">{trailingIcon}</span>}
        </>
      )}
    </motion.button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
    />
  );
}
